import { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ErroDaApi } from '../../../shared/api/erros';
import { Identificador } from '../../../shared/ui/Identificador';
import { chavesDeContratos, enviarFonte, opcoesDaFonte } from '../api/contratos';
import {
  ROTULOS_DE_TIPO_DE_FONTE,
  TIPOS_DE_FONTE,
  TIPOS_DE_FONTE_AINDA_NAO_VALIDADOS,
  type TipoDeFonte,
} from '../api/tipos';
import { SeloDeEstadoDaFonte } from './SeloDeEstadoDaFonte';

/**
 * Espelha `SourceSubmissionOptions.MaxSizeBytes` (`apps/api`, default `50 *
 * 1024 * 1024`). A API não expõe o limite configurado por um endpoint — o
 * pré-cheque do front usa o mesmo valor do default, documentado aqui de
 * propósito: se a configuração mudar em produção, o pré-cheque do front sai
 * de sincronia, mas a API continua sendo a autoridade final (ela confere de
 * novo, e o motivo da recusa do servidor também aparece nesta tela).
 */
export const LIMITE_DE_TAMANHO_BYTES = 50 * 1024 * 1024;

const ASSINATURA_PDF = '%PDF';

async function lerAssinatura(arquivo: File): Promise<string> {
  const bytes = new Uint8Array(await arquivo.slice(0, 4).arrayBuffer());
  return String.fromCharCode(...bytes);
}

/** Pré-cheque do front: extensão, tamanho e assinatura, nessa ordem. `null` = passou. */
async function validarArquivo(arquivo: File): Promise<string | null> {
  if (arquivo.size > LIMITE_DE_TAMANHO_BYTES) {
    return `Arquivo grande demais: o limite é ${String(Math.floor(LIMITE_DE_TAMANHO_BYTES / (1024 * 1024)))} MB.`;
  }
  if (!arquivo.name.toLowerCase().endsWith('.pdf')) {
    return 'Esse arquivo não é um PDF (a extensão não é .pdf).';
  }
  const assinatura = await lerAssinatura(arquivo);
  if (assinatura !== ASSINATURA_PDF) {
    return 'Esse arquivo não é um PDF (o conteúdo não começa com a assinatura de um PDF).';
  }
  return null;
}

function formatarTamanho(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Envio do PDF de uma fonte do contrato (`TP-049`, T4/T9).
 *
 * Confere extensão, tamanho e assinatura ANTES de qualquer requisição — um
 * arquivo que não passa no pré-cheque nunca sai desta tela. A API confere de
 * novo (ela é a autoridade: `D6`, `F3`), e o motivo que ela devolver também é
 * mostrado aqui, sem distinção de onde veio a recusa.
 */
export function EnvioDeFonte({ contratoId }: { contratoId: string }): React.JSX.Element {
  const navegar = useNavigate();
  const cliente = useQueryClient();

  const [arquivo, definirArquivo] = useState<File | null>(null);
  const [nomeDoArquivoRecusado, definirNomeDoArquivoRecusado] = useState<string | null>(null);
  const [motivoDaRecusaLocal, definirMotivoDaRecusaLocal] = useState<string | null>(null);
  const [tipo, definirTipo] = useState<TipoDeFonte>('contrato_base');
  const [vigenciaInicio, definirVigenciaInicio] = useState('');
  const [vigenciaFim, definirVigenciaFim] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erroDoEnvio, definirErroDoEnvio] = useState<string | null>(null);
  const [resultado, definirResultado] = useState<{ fonteId: string; criada: boolean } | null>(null);
  const entradaDeArquivo = useRef<HTMLInputElement>(null);
  const [arrastando, definirArrastando] = useState(false);

  const detalheDaFonteEnviada = useQuery({
    ...opcoesDaFonte(resultado?.fonteId ?? ''),
    enabled: resultado !== null,
  });

  const limparSelecao = (): void => {
    definirArquivo(null);
    definirNomeDoArquivoRecusado(null);
    definirMotivoDaRecusaLocal(null);
    definirResultado(null);
    definirErroDoEnvio(null);
  };

  const selecionar = (lista: FileList | null): void => {
    const escolhido = lista?.[0];
    if (escolhido === undefined) return;
    definirResultado(null);
    definirErroDoEnvio(null);
    void validarArquivo(escolhido).then((motivo) => {
      if (motivo !== null) {
        definirArquivo(null);
        definirNomeDoArquivoRecusado(escolhido.name);
        definirMotivoDaRecusaLocal(motivo);
        return;
      }
      definirArquivo(escolhido);
      definirNomeDoArquivoRecusado(null);
      definirMotivoDaRecusaLocal(null);
    });
  };

  const enviar = (): void => {
    if (arquivo === null || vigenciaInicio.trim() === '') return;
    definirEnviando(true);
    definirErroDoEnvio(null);
    definirResultado(null);
    enviarFonte({ contratoId, arquivo, tipo, vigenciaInicio, vigenciaFim })
      .then((feito) => {
        definirResultado(feito);
        void cliente.invalidateQueries({ queryKey: chavesDeContratos.fontes(contratoId) });
      })
      .catch((causa: unknown) => {
        if (causa instanceof ErroDaApi && causa.status === 401) {
          void navegar('/entrar', { replace: true });
          return;
        }
        definirErroDoEnvio(
          causa instanceof ErroDaApi ? causa.message : 'Não foi possível enviar o arquivo agora. Tente de novo em alguns instantes.',
        );
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  const podeEnviar = arquivo !== null && vigenciaInicio.trim() !== '' && !enviando;
  const faltando = [
    arquivo === null ? 'escolher o PDF' : null,
    vigenciaInicio.trim() === '' ? 'preencher a vigência (início)' : null,
  ].filter((item): item is string => item !== null);

  return (
    <Stack spacing={1.5} component="form" aria-label="Enviar o PDF do contrato" onSubmit={(evento) => { evento.preventDefault(); enviar(); }}>
      <Typography variant="h3">Enviar contrato</Typography>

      <Box
        role="button"
        tabIndex={0}
        aria-label="Área para arrastar ou escolher o PDF"
        onClick={() => entradaDeArquivo.current?.click()}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' || evento.key === ' ') entradaDeArquivo.current?.click();
        }}
        onDragOver={(evento) => {
          evento.preventDefault();
          definirArrastando(true);
        }}
        onDragLeave={() => {
          definirArrastando(false);
        }}
        onDrop={(evento) => {
          evento.preventDefault();
          definirArrastando(false);
          selecionar(evento.dataTransfer.files);
        }}
        sx={(tema) => ({
          border: `2px ${arquivo !== null ? 'solid' : 'dashed'} ${
            arrastando || arquivo !== null ? tema.palette.sla.ok.borda : tema.palette.divider
          }`,
          backgroundColor: arrastando || arquivo !== null ? tema.palette.sla.ok.fundo : 'transparent',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
        })}
      >
        <input
          ref={entradaDeArquivo}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          aria-label="Escolher o PDF do contrato"
          // O input fica dentro da área clicável: sem isto, o clique dele sobe
          // para a área e chama `click()` de novo enquanto a janela de escolha abre.
          onClick={(evento) => {
            evento.stopPropagation();
          }}
          onChange={(evento) => {
            selecionar(evento.target.files);
            // Sem limpar, escolher o MESMO arquivo de novo não dispara `change`.
            evento.target.value = '';
          }}
        />
        {arquivo !== null ? (
          <>
            <CheckCircleOutlined sx={(tema) => ({ fontSize: 32, color: tema.palette.sla.ok.borda })} />
            <Typography variant="body2" fontWeight={600}>
              Arquivo escolhido
            </Typography>
            <Typography variant="body2">
              {arquivo.name} ({formatarTamanho(arquivo.size)})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Clique para trocar.
            </Typography>
          </>
        ) : (
          <>
            <UploadFileOutlined sx={(tema) => ({ fontSize: 32, color: tema.palette.text.secondary })} />
            <Typography variant="body2">Arraste o PDF aqui, ou clique para escolher o arquivo.</Typography>
          </>
        )}
      </Box>

      {motivoDaRecusaLocal !== null ? (
        <Alert severity="warning" role="alert">
          {nomeDoArquivoRecusado !== null ? `"${nomeDoArquivoRecusado}": ` : ''}
          {motivoDaRecusaLocal} Nada foi enviado.
        </Alert>
      ) : null}

      <TextField
        select
        label="Tipo da fonte"
        value={tipo}
        onChange={(evento) => {
          definirTipo(evento.target.value as TipoDeFonte);
        }}
        size="small"
        sx={{ maxWidth: 280 }}
      >
        {TIPOS_DE_FONTE.map((valor) => (
          <MenuItem key={valor} value={valor} disabled={TIPOS_DE_FONTE_AINDA_NAO_VALIDADOS.has(valor)}>
            {ROTULOS_DE_TIPO_DE_FONTE[valor]}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction="row" spacing={1.5}>
        <TextField
          label="Vigência (início)"
          type="date"
          size="small"
          value={vigenciaInicio}
          onChange={(evento) => {
            definirVigenciaInicio(evento.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
          required
        />
        <TextField
          label="Vigência (fim, opcional)"
          type="date"
          size="small"
          value={vigenciaFim}
          onChange={(evento) => {
            definirVigenciaFim(evento.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      <Box>
        <Button type="submit" variant="contained" disabled={!podeEnviar}>
          Enviar
        </Button>
        {arquivo !== null || resultado !== null ? (
          <Button
            variant="text"
            sx={{ ml: 1 }}
            onClick={() => {
              limparSelecao();
            }}
          >
            Limpar
          </Button>
        ) : null}
        {faltando.length > 0 && resultado === null ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Para enviar, falta {faltando.join(' e ')}.
          </Typography>
        ) : null}
      </Box>

      {erroDoEnvio !== null ? <Alert severity="error" role="alert">{erroDoEnvio}</Alert> : null}

      {resultado !== null ? (
        <Alert severity={resultado.criada ? 'success' : 'info'} role="status">
          <Stack spacing={0.5}>
            <Typography variant="body2">
              {resultado.criada
                ? 'Fonte enviada.'
                : 'Esse arquivo já tinha sido enviado antes. Esta é a fonte existente.'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Fonte:
              </Typography>
              <Identificador valor={resultado.fonteId} />
              {detalheDaFonteEnviada.data !== undefined ? (
                <SeloDeEstadoDaFonte estado={detalheDaFonteEnviada.data.estado} />
              ) : null}
            </Stack>
            {detalheDaFonteEnviada.data?.estado === 'rejeitada' && detalheDaFonteEnviada.data.motivoDaRecusa !== null ? (
              <Typography variant="body2" color="error.main">
                Motivo: {detalheDaFonteEnviada.data.motivoDaRecusa}
              </Typography>
            ) : null}
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
}
